/**
 * LandingPage — warm/earthy redesign.
 *
 * Visual direction: "Sunday morning calm." Light cream background, terracotta
 * accents, serif headings (Cormorant Garamond), Manrope body. Less neon AI
 * marketing, more humanized — addresses all 3 personas (churches, educators,
 * presenters) via a self-identify band right under the hero.
 *
 * Data-testids preserved verbatim per the design blueprint.
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight, Sparkles, Music, ListMusic, Calendar, Mic, FileText, Search,
  MessageSquare, Scissors, BookMarked, Presentation, ScanLine, Monitor,
  Plug, Users, Quote, Menu, X, Camera, Bell,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Sheet, SheetContent, SheetTrigger,
} from "@/components/ui/sheet";
import { useLanguage } from "@/contexts/LanguageContext";
import { LanguageSelector } from "@/components/LanguageSelector";
import HeroAnimatedSlide from "@/components/landing/HeroAnimatedSlide";
import CommunityVignette from "@/components/landing/CommunityVignette";

const HERO_IMG = "https://images.unsplash.com/photo-1552600552-4d7c157e7db8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHwxfHxjaHVyY2glMjBlbXB0eSUyMG1vcm5pbmclMjBsaWdodHxlbnwwfHx8fDE3ODI3NTc4NTF8MA&ixlib=rb-4.1.0&q=85";
const PERSONA_CHURCH = "https://images.unsplash.com/photo-1525026198548-4baa812f1183?auto=format&fit=crop&w=800&q=80";
const PERSONA_EDUCATOR = "https://images.pexels.com/photos/37758126/pexels-photo-37758126.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const PERSONA_PRESENTER = "https://images.pexels.com/photos/20733081/pexels-photo-20733081.jpeg";
const TESTIMONIAL_PORTRAIT = "https://images.unsplash.com/photo-1544005313-94ddf0286df2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHwxfHxwb3J0cmFpdCUyMG5hdHVyYWwlMjBsaWdodHxlbnwwfHx8fDE3ODI3NTc4NTF8MA&ixlib=rb-4.1.0&q=85";

const PERSONAS = [
  {
    img: PERSONA_CHURCH,
    overline: "Worship Leaders",
    title: "Lead with confidence",
    body: "Setlists you can drag-and-drop. Lyrics formatted in a tap. CCLI imports. Service planning that survives Saturday-night changes.",
    chips: ["Setlists", "CCLI Import", "Song Mode"],
  },
  {
    img: PERSONA_EDUCATOR,
    overline: "Educators",
    title: "Teach without the tech headache",
    body: "Paste a lesson, scan a handout, or upload a doc — get a clean, classroom-ready deck. Multi-language UI for international classrooms.",
    chips: ["AI Slides", "Doc Scanner", "7 Languages"],
  },
  {
    img: PERSONA_PRESENTER,
    overline: "Presenters",
    title: "Broadcast anywhere",
    body: "Live streaming to OBS or YouTube. PowerPoint/PDF exports. Drawing & highlighter on top of any slide. A real desktop app, not just a tab.",
    chips: ["OBS Streaming", "Exports", "Desktop App"],
  },
];

const FEATURE_GROUPS = [
  {
    label: "PreachKit · Sermon prep",
    accent: "lp-tag-sage",
    items: [
      { icon: Sparkles,     title: "Sermon Outline AI",     body: "Type a topic or verse — get a 3-point outline with introduction, illustrations, application & CTA." },
      { icon: BookMarked,   title: "Scripture → Slides",    body: "Paste any passage; AI builds a ready-to-present deck and ships it to the editor." },
      { icon: Search,       title: "Bible Search",          body: "Find verses by theme with AI cross-references. Tap a reference to expand the full text." },
      { icon: MessageSquare,title: "Sermon Companion",      body: "Multi-turn chat for Greek/Hebrew word studies, historical context, and theology questions." },
      { icon: Scissors,     title: "Sermon Video Clipper",  body: "Upload Sunday's sermon; AI picks 5 highlight moments and renders 9:16 + 1:1 with optional burnt-in captions." },
      { icon: FileText,     title: "Notes → Sermon",        body: "Bullet points or scribbles? Get them organized into a polished outline that preserves your best quotes." },
    ],
  },
  {
    label: "Worship",
    accent: "lp-tag-sage",
    items: [
      { icon: Music,    title: "Song Mode",        body: "One toggle re-formats raw lyrics into reverent worship slides — verse/chorus aware." },
      { icon: ListMusic,title: "Drag-and-Drop Setlists", body: "Rearrange your Sunday set without a single right-click." },
      { icon: Calendar, title: "Service Calendar", body: "Plan multiple weeks ahead. Reminders are on our P1 roadmap." },
    ],
  },
  {
    label: "Education & presentation",
    accent: "lp-tag-sage",
    items: [
      { icon: Sparkles,    title: "AI Slide Generator", body: "Paste any text → get a classroom-ready deck in seconds." },
      { icon: ScanLine,    title: "Document Scanner",   body: "Mobile-friendly scanning + enhancement for handouts and notes." },
      { icon: Mic,         title: "Live Streaming",     body: "Stream your slides into OBS or YouTube via a browser source URL." },
      { icon: Presentation,title: "Highlighter + Editor",body: "Annotate live. Save annotations. Export to PowerPoint or PDF." },
    ],
  },
  {
    label: "Community & production",
    accent: "lp-tag-sage",
    items: [
      { icon: Users,    title: "Real-time DMs & Photos", body: "Connect with your team — chat, share photos, see who's online." },
      { icon: Monitor,  title: "Desktop Apps",           body: "Native Electron builds for Windows, macOS and Linux." },
      { icon: Plug,     title: "Public API v1",          body: "Partner tools (like Lumera) can drop a sermon PDF in and pull back an editable slide deck." },
    ],
  },
];

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] },
};

export default function LandingPage() {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  const mobileGo = (path) => { setMobileNavOpen(false); navigate(path); };

  return (
    <div className="landing-page relative min-h-screen">
      {/* ─── Header ─────────────────────────────────────────────────────── */}
      <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-xl border-b border-stone-200/60">
        <nav className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: "#C47053" }}>
              <FileText className="w-4 h-4 text-white" />
            </div>
            <div className="flex flex-col leading-none">
              <span className="font-display text-2xl font-semibold">SlideCon</span>
              <span className="hidden sm:inline text-[10px] tracking-[0.18em] uppercase text-stone-500 font-medium mt-0.5">
                Slides &middot; Connection
              </span>
            </div>
          </a>

          {/* Desktop nav (≥ md) */}
          <div className="hidden md:flex items-center gap-1">
            <LanguageSelector variant="ghost" />
            <Button
              variant="ghost"
              onClick={() => navigate("/download")}
              className="text-stone-700 hover:bg-stone-100 hover:text-stone-900 rounded-full"
              data-testid="nav-download-btn"
            >
              {t("download") || "Download"}
            </Button>
            <Button
              variant="ghost"
              onClick={() => navigate("/pricing")}
              className="text-stone-700 hover:bg-stone-100 hover:text-stone-900 rounded-full"
              data-testid="nav-pricing-btn"
            >
              {t("pricing_plans") || "Pricing"}
            </Button>
            <Button
              variant="ghost"
              onClick={() => navigate("/collaborators")}
              className="text-stone-700 hover:bg-stone-100 hover:text-stone-900 rounded-full"
              data-testid="nav-collaborators-btn"
            >
              {t("collaborators") || "Affiliates"}
            </Button>
            <Button
              onClick={() => navigate("/dashboard")}
              className="lp-btn-primary ml-1"
              data-testid="nav-get-started-btn"
            >
              {t("get_started") || "Get Started"}
            </Button>
          </div>

          {/* Mobile hamburger (< md) */}
          <div className="md:hidden flex items-center gap-1">
            <LanguageSelector variant="ghost" />
            <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
              <SheetTrigger asChild>
                <button
                  type="button"
                  aria-label="Open menu"
                  className="w-10 h-10 inline-flex items-center justify-center rounded-full text-stone-700 hover:bg-stone-100"
                  data-testid="mobile-nav-trigger"
                >
                  <Menu className="w-5 h-5" />
                </button>
              </SheetTrigger>
              <SheetContent side="right" className="w-72 bg-[--lp-bg-1] border-stone-200" style={{ backgroundColor: "#FDFBF7" }}>
                <div className="flex items-center justify-between mb-8 mt-1">
                  <span className="font-display text-xl font-semibold">SlideCon</span>
                  <button
                    type="button"
                    onClick={() => setMobileNavOpen(false)}
                    aria-label="Close menu"
                    className="w-9 h-9 inline-flex items-center justify-center rounded-full text-stone-700 hover:bg-stone-100"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
                <div className="flex flex-col gap-1">
                  <button onClick={() => mobileGo("/download")} className="text-left px-3 py-3 rounded-lg text-stone-700 hover:bg-stone-100" data-testid="mobile-nav-download">{t("download") || "Download"}</button>
                  <button onClick={() => mobileGo("/pricing")} className="text-left px-3 py-3 rounded-lg text-stone-700 hover:bg-stone-100" data-testid="mobile-nav-pricing">{t("pricing_plans") || "Pricing"}</button>
                  <button onClick={() => mobileGo("/collaborators")} className="text-left px-3 py-3 rounded-lg text-stone-700 hover:bg-stone-100" data-testid="mobile-nav-collaborators">{t("collaborators") || "Affiliates"}</button>
                  <Button
                    onClick={() => mobileGo("/dashboard")}
                    className="lp-btn-primary mt-4 w-full justify-center"
                    data-testid="mobile-nav-get-started"
                  >
                    {t("get_started") || "Get Started"}
                  </Button>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </nav>
      </header>

      {/* ─── Hero ───────────────────────────────────────────────────────── */}
      <section className="lp-bg-soft relative overflow-hidden">
        <div className="lp-noise" />
        <div className="max-w-6xl mx-auto px-6 py-20 lg:py-28 grid lg:grid-cols-[1.05fr_1fr] gap-12 lg:gap-16 items-center">
          <motion.div {...fadeUp}>
            <p className="lp-overline mb-5">Built for churches, educators, and presenters.</p>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-semibold leading-[1.05] mb-6">
              Sunday morning calm.
              <span className="block italic text-stone-700">Any day of the week.</span>
            </h1>
            <p className="text-lg text-stone-600 leading-relaxed max-w-xl mb-10">
              Slides + Connection. Prep on Saturday, present on Sunday, and stay in touch with your
              team the whole week &mdash; DMs, photos, setlists, and service planning in one calm place.
            </p>
            <div className="flex flex-wrap items-center gap-3">
              <Button
                onClick={() => navigate("/demo")}
                className="lp-btn-primary text-base gap-2"
                data-testid="hero-try-demo-btn"
              >
                <Sparkles className="w-4 h-4" />
                Try the live demo
              </Button>
              <Button
                onClick={() => navigate("/dashboard")}
                className="lp-btn-ghost text-base gap-2"
                data-testid="hero-get-started-btn"
              >
                {t("get_started") || "Get started free"}
                <ArrowRight className="w-4 h-4" />
              </Button>
              <button
                type="button"
                onClick={() => {
                  document.getElementById("features")?.scrollIntoView({ behavior: "smooth" });
                }}
                className="text-sm text-stone-500 hover:text-stone-800 underline underline-offset-4 ml-1"
                data-testid="hero-learn-more-btn"
              >
                {t("learn_more") || "or see what's inside"}
              </button>
            </div>
            <div className="mt-12 flex flex-wrap gap-x-10 gap-y-3 text-sm">
              <Stat label="File formats" value="PDF · DOCX · PPTX · TXT" />
              <Stat label="UI languages" value="7" />
              <Stat label="Pricing" value="Free to start" />
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="relative"
          >
            <img
              src={HERO_IMG}
              alt="Warm sanctuary at morning, sunlight falling on empty wooden pews"
              loading="eager"
              className="w-full aspect-[5/6] object-cover rounded-2xl shadow-[0_24px_60px_-20px_rgba(28,25,23,0.25)]"
            />
            {/* Floating animated preview — cycles through Input → Outline → Building → Slide */}
            <div className="absolute -left-4 lg:-left-10 bottom-6 hidden sm:block">
              <HeroAnimatedSlide />
            </div>
          </motion.div>
        </div>
      </section>

      {/* ─── Personas Band ──────────────────────────────────────────────── */}
      <section className="bg-[--lp-bg-2] py-20" style={{ backgroundColor: "#FAF6F0" }}>
        <div className="max-w-6xl mx-auto px-6">
          <motion.div {...fadeUp} className="mb-12 max-w-2xl">
            <p className="lp-overline mb-3">Who it&apos;s for</p>
            <h2 className="font-display text-4xl sm:text-5xl font-semibold leading-tight">
              Pick the shoes that fit.
            </h2>
            <p className="text-stone-600 mt-4">
              SlideCon was built alongside worship pastors, classroom teachers and conference speakers.
              The interface adapts to your craft.
            </p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {PERSONAS.map((p, i) => (
              <motion.article
                key={p.title}
                {...fadeUp}
                transition={{ ...fadeUp.transition, delay: i * 0.1 }}
                className="lp-card-solid overflow-hidden flex flex-col"
                data-testid={`persona-card-${i}`}
              >
                <div className="aspect-[5/4] overflow-hidden bg-stone-100">
                  <img src={p.img} alt="" loading="lazy" className="w-full h-full object-cover" />
                </div>
                <div className="p-6 flex-1 flex flex-col">
                  <p className="lp-overline mb-2">{p.overline}</p>
                  <h3 className="font-display text-2xl font-semibold mb-2 text-stone-900">{p.title}</h3>
                  <p className="text-sm text-stone-600 leading-relaxed flex-1">{p.body}</p>
                  <div className="flex flex-wrap gap-1.5 mt-4">
                    {p.chips.map((chip) => (
                      <span key={chip} className="text-[11px] px-2 py-1 rounded-full bg-stone-100 text-stone-700 font-medium">
                        {chip}
                      </span>
                    ))}
                  </div>
                </div>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Features Showcase ──────────────────────────────────────────── */}
      <section id="features" className="bg-[--lp-bg-1] py-24" style={{ backgroundColor: "#FDFBF7" }}>
        <div className="max-w-6xl mx-auto px-6">
          <motion.div {...fadeUp} className="mb-16 max-w-2xl">
            <p className="lp-overline mb-3">Everything inside</p>
            <h2 className="font-display text-4xl sm:text-5xl font-semibold leading-tight">
              One craft. Many tools.
            </h2>
            <p className="text-stone-600 mt-4">
              From a single dashboard you can prep a sermon, run a worship setlist, teach a class,
              stream to OBS, and ship a Reels-ready clip on Monday morning.
            </p>
          </motion.div>

          <div className="space-y-16">
            {FEATURE_GROUPS.map((group, gi) => (
              <motion.div key={group.label} {...fadeUp}>
                <div className="flex items-center gap-3 mb-6">
                  <span className={`lp-overline px-2.5 py-1 rounded-full ${group.accent}`}>
                    {group.label}
                  </span>
                  <div className="flex-1 lp-divider" />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                  {group.items.map((feat, fi) => {
                    const Icon = feat.icon;
                    const flatIdx = FEATURE_GROUPS.slice(0, gi).reduce((acc, g) => acc + g.items.length, 0) + fi;
                    return (
                      <motion.div
                        key={feat.title}
                        initial={{ opacity: 0, y: 14 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.45, delay: fi * 0.04 }}
                        className="lp-card-solid p-6 hover:-translate-y-0.5 hover:shadow-[0_8px_24px_rgba(28,25,23,0.06)] transition-all"
                        data-testid={`feature-card-${flatIdx}`}
                      >
                        <div
                          className="w-11 h-11 rounded-xl flex items-center justify-center mb-4"
                          style={{ backgroundColor: "rgba(196, 112, 83, 0.10)" }}
                        >
                          <Icon className="w-5 h-5" style={{ color: "#C47053" }} />
                        </div>
                        <h3 className="font-display text-xl font-semibold mb-1.5 text-stone-900">
                          {feat.title}
                        </h3>
                        <p className="text-sm text-stone-600 leading-relaxed">{feat.body}</p>
                      </motion.div>
                    );
                  })}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── "More than slides" — Community & Connection band ──────────── */}
      <section className="py-24 relative overflow-hidden" style={{ backgroundColor: "#1C1917" }}>
        <div className="absolute inset-0 opacity-20 pointer-events-none"
          style={{
            background:
              "radial-gradient(50% 60% at 20% 30%, rgba(196, 112, 83, 0.35), transparent 70%), radial-gradient(50% 60% at 90% 80%, rgba(138, 154, 134, 0.20), transparent 70%)",
          }}
        />
        <div className="max-w-6xl mx-auto px-6 relative">
          <motion.div {...fadeUp} className="max-w-3xl mb-12">
            <p className="lp-overline mb-3" style={{ color: "#EADEC9" }}>
              The &ldquo;Con&rdquo; in SlideCon
            </p>
            <h2 className="font-display text-4xl sm:text-5xl font-semibold leading-tight text-stone-50 mb-5">
              More than slides. It&apos;s <em className="text-[#EADEC9]">Slides + Connection.</em>
            </h2>
            <p className="text-stone-300 text-lg leading-relaxed">
              Sunday only happens once a week. Your team, your students, your congregation are with you the
              whole seven days. SlideCon is where the messages, the setlists, the photos and the plans all live &mdash;
              side by side with the slides you present.
            </p>
          </motion.div>

          <CommunityVignette />

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { icon: MessageSquare, title: "Real-time Direct Messages",
                body: "Chat one-on-one with worship team members, students, or fellow teachers. Push notifications, read receipts, sound cues." },
              { icon: Users, title: "Church community feed",
                body: "A private feed for your congregation. Share prayer requests, photos, updates and announcements without leaving SlideCon." },
              { icon: Camera, title: "Shared photo galleries",
                body: "Sunday photos, retreat memories, classroom moments — posted, enhanced, and shared with the community." },
              { icon: ListMusic, title: "Setlist planning",
                body: "Drag-and-drop worship setlists your whole team can see. Assign songs, reorder on the fly, ship to Sunday screens." },
              { icon: Calendar, title: "Service & schedule organizing",
                body: "Plan multiple services or class sessions ahead. Everyone sees the same source of truth." },
              { icon: Bell, title: "Push notifications",
                body: "Native push when a DM arrives, when a setlist changes, when someone tags you in a photo. On desktop and mobile." },
            ].map((item, i) => {
              const Icon = item.icon;
              return (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, y: 12 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.06 }}
                  className="rounded-2xl p-6 border border-white/10 bg-white/[0.03] hover:bg-white/[0.06] transition-colors"
                  data-testid={`community-card-${i}`}
                >
                  <div
                    className="w-10 h-10 rounded-xl flex items-center justify-center mb-4"
                    style={{ backgroundColor: "rgba(234, 222, 201, 0.10)" }}
                  >
                    <Icon className="w-5 h-5" style={{ color: "#EADEC9" }} />
                  </div>
                  <h3 className="font-display text-xl font-semibold mb-1.5 text-stone-50">
                    {item.title}
                  </h3>
                  <p className="text-sm text-stone-400 leading-relaxed">{item.body}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ─── Lumera / Public API Callout ────────────────────────────────── */}
      <section className="py-20" style={{ backgroundColor: "#EADEC9" }}>
        <div className="max-w-5xl mx-auto px-6 grid lg:grid-cols-[1.1fr_1fr] gap-12 items-center">
          <motion.div {...fadeUp}>
            <div className="lp-pill mb-4"><Plug className="w-3 h-3" /> Public API v1</div>
            <h2 className="font-display text-4xl sm:text-5xl font-semibold leading-tight mb-4">
              Plays beautifully with others.
            </h2>
            <p className="text-stone-700 leading-relaxed mb-6">
              Partner tools like the Lumera Bible app can drop a sermon PDF straight into SlideCon
              and receive an editable slide deck URL in seconds. No copy-paste. No format dance.
            </p>
            <div className="flex flex-wrap gap-3">
              <Button
                onClick={() => navigate("/dashboard")}
                className="lp-btn-primary text-sm"
                data-testid="api-cta-btn"
              >
                Get your API key
                <ArrowRight className="w-4 h-4 ml-1.5" />
              </Button>
              <a
                href={`${process.env.REACT_APP_BACKEND_URL || ""}/api/docs`}
                target="_blank"
                rel="noopener noreferrer"
                className="lp-btn-ghost text-sm inline-flex items-center"
                data-testid="api-docs-cta"
              >
                Read the docs
              </a>
            </div>
          </motion.div>
          <motion.div
            {...fadeUp}
            transition={{ ...fadeUp.transition, delay: 0.15 }}
            className="lp-card-solid p-5 overflow-x-auto"
          >
            <p className="lp-overline mb-2">curl example</p>
            <pre className="text-[12px] leading-relaxed text-stone-800 font-mono whitespace-pre">
{`curl -X POST https://slidecon.app/api/v1/projects/from-pdf \\
  -H "X-API-Key: slcn_live_..." \\
  -F "file=@sermon.pdf" \\
  -F "source_app=lumera"

# → { project_id, edit_url, view_url, slide_count }`}
            </pre>
          </motion.div>
        </div>
      </section>

      {/* ─── Testimonial ────────────────────────────────────────────────── */}
      <section className="py-24" style={{ backgroundColor: "#FAF6F0" }}>
        <div className="max-w-5xl mx-auto px-6 grid lg:grid-cols-[1fr_1.4fr] gap-12 items-center">
          <motion.div {...fadeUp} className="relative">
            <img
              src={TESTIMONIAL_PORTRAIT}
              alt="Worship pastor portrait, soft natural daylight"
              loading="lazy"
              className="w-full aspect-[4/5] object-cover rounded-2xl shadow-[0_20px_50px_-20px_rgba(28,25,23,0.25)]"
            />
            <div className="absolute -bottom-4 -right-4 lp-card-solid px-4 py-3 text-sm">
              <p className="font-semibold text-stone-900">Sarah Mitchell</p>
              <p className="text-stone-500 text-xs">Worship Pastor · Grace Church</p>
            </div>
          </motion.div>
          <motion.blockquote {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.15 }}>
            <Quote className="w-10 h-10 text-stone-300 mb-4" />
            <p className="font-display text-3xl sm:text-4xl leading-tight text-stone-800 mb-6">
              &ldquo;SlideCon gave me my Saturday nights back. I plan the whole worship set on my couch,
              and Sunday morning I just show up and lead.&rdquo;
            </p>
            <p className="text-sm text-stone-500">
              Used by worship teams, classroom teachers, and conference speakers across 28 countries.
            </p>
          </motion.blockquote>
        </div>
      </section>

      {/* ─── Pricing teaser + CTA ───────────────────────────────────────── */}
      <section className="py-24 lp-bg-soft relative overflow-hidden">
        <div className="lp-noise" />
        <div className="max-w-3xl mx-auto px-6 text-center">
          <motion.div {...fadeUp}>
            <p className="lp-overline mb-4">Ready when you are</p>
            <h2 className="font-display text-4xl sm:text-5xl font-semibold leading-tight mb-5">
              Slides, connection, and calm &mdash; all week.
            </h2>
            <p className="text-stone-600 mb-8 text-lg leading-relaxed">
              Free to start. Upgrade only if you need more AI generations, more projects,
              or the whole team in one place.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-3">
              <Button
                onClick={() => navigate("/dashboard")}
                className="lp-btn-primary text-base gap-2"
                data-testid="cta-get-started-btn"
              >
                Get started — free
                <ArrowRight className="w-4 h-4" />
              </Button>
              <Button
                onClick={() => navigate("/pricing")}
                className="lp-btn-ghost text-base"
                data-testid="cta-pricing-btn"
              >
                See pricing
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ─── Footer ─────────────────────────────────────────────────────── */}
      <footer className="bg-[--lp-bg-2] border-t border-stone-200/60" style={{ backgroundColor: "#FAF6F0" }}>
        <div className="max-w-6xl mx-auto px-6 py-12">
          <div className="grid md:grid-cols-3 gap-8 mb-10">
            <div>
              <div className="flex items-center gap-2.5 mb-3">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: "#C47053" }}>
                  <FileText className="w-4 h-4 text-white" />
                </div>
                <span className="font-display text-xl font-semibold">SlideCon</span>
              </div>
              <p className="text-sm text-stone-600 leading-relaxed">
                Built for churches, educators, and presenters.
              </p>
            </div>
            <div className="text-sm">
              <p className="lp-overline mb-3">Product</p>
              <ul className="space-y-2 text-stone-600">
                <li><a href="/pricing" className="hover:text-stone-900">Pricing</a></li>
                <li><a href="/download" className="hover:text-stone-900">Download</a></li>
                <li><a href="/preach-kit" className="hover:text-stone-900">PreachKit</a></li>
                <li><a href={`${process.env.REACT_APP_BACKEND_URL || ""}/api/docs`} target="_blank" rel="noopener noreferrer" className="hover:text-stone-900">API docs</a></li>
              </ul>
            </div>
            <div className="text-sm">
              <p className="lp-overline mb-3">Legal</p>
              <ul className="space-y-2 text-stone-600">
                <li><a href="/terms" className="hover:text-stone-900">{t("terms_of_service") || "Terms of Service"}</a></li>
                <li><a href="/privacy" className="hover:text-stone-900">{t("privacy_policy") || "Privacy Policy"}</a></li>
                <li><a href="/acceptable-use" className="hover:text-stone-900">{t("acceptable_use") || "Acceptable Use"}</a></li>
              </ul>
            </div>
          </div>
          <div className="lp-divider mb-6" />
          <p className="text-xs text-stone-500 text-center">
            © {new Date().getFullYear()} SlideCon. All rights reserved. California, USA.
          </p>
        </div>
      </footer>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div className="text-stone-900 font-semibold text-base">{value}</div>
      <div className="text-stone-500 text-xs uppercase tracking-wider mt-0.5">{label}</div>
    </div>
  );
}
